import {
  calcHasFacilityViolations,
  hasActiveFacilityConstraints,
  stateHasDraws,
  summarizeConstraintViolations,
} from './constraintViolations';
import {
  isMarketingPlanManualMode,
  resolveEffectiveFundingPct,
} from './resolvePlanFunding';
import {
  buildStateWithProRataRevShare,
  proposeFundingFixes,
} from './proposeFundingFixes';
import { runCalc } from './runCalc';

function shouldAttemptAutoFix(state) {
  if (!state.autoFixFundingEnabled) {
    return false;
  }
  if (!hasActiveFacilityConstraints(state)) {
    return false;
  }
  if (!stateHasDraws(state)) {
    return false;
  }
  return true;
}

/** Sync fundingPct from manual planDrawAmount so proposeFundingFixes can operate. */
function prepareStateForFundingFix(state) {
  if (!isMarketingPlanManualMode(state)) {
    return state;
  }
  const fundingPct = state.marketing.map((_, planIndex) =>
    resolveEffectiveFundingPct(state, planIndex)
  );
  return { ...state, fundingPct };
}

function buildCalcStateFromProposal(state, proposal, originalFundingPct) {
  const fixInputState = prepareStateForFundingFix(state);
  const syncedState = buildStateWithProRataRevShare(
    fixInputState,
    proposal.proposedFundingPct,
    originalFundingPct
  );

  if (!isMarketingPlanManualMode(state)) {
    return {
      ...state,
      fundingPct: syncedState.fundingPct,
      revSharePct: syncedState.revSharePct,
    };
  }

  return {
    ...state,
    planDrawAmount: syncedState.planDrawAmount,
    revSharePct: syncedState.revSharePct,
  };
}

function buildAutoFixMeta({
  attempted,
  applied,
  fullyFixed,
  changeCount,
  firstRemainingViolation,
}) {
  if (!attempted) {
    return null;
  }
  return {
    attempted: true,
    applied,
    fullyFixed,
    changeCount,
    firstRemainingViolation,
  };
}

function firstViolationSummary(state, calcResult, exposureGradeLimits) {
  const { summaries } = summarizeConstraintViolations(
    state,
    calcResult,
    exposureGradeLimits
  );
  return summaries[0] ?? null;
}

/**
 * Runs the cashflow calc, auto-adjusting funding/rev-share at calc time when limits
 * are breached. Does not mutate the input state.
 */
export function runCalcWithAutoFundingFix(
  state,
  exposureGradeLimits,
  planMonthLabels = []
) {
  const baselineResult = runCalc(state);
  const attemptAutoFix = shouldAttemptAutoFix(state);
  const baselineHasFacilityViolations = calcHasFacilityViolations(state, baselineResult);

  if (!attemptAutoFix || !baselineHasFacilityViolations) {
    return { calcResult: baselineResult, autoFix: null };
  }

  const fixInputState = prepareStateForFundingFix(state);
  const originalFundingPct = fixInputState.fundingPct.map(
    (value) => Number(value) || 0
  );
  const proposal = proposeFundingFixes(
    fixInputState,
    exposureGradeLimits,
    planMonthLabels
  );

  if (!proposal.changes.length) {
    return {
      calcResult: baselineResult,
      autoFix: buildAutoFixMeta({
        attempted: true,
        applied: false,
        fullyFixed: false,
        changeCount: 0,
        firstRemainingViolation: firstViolationSummary(
          state,
          baselineResult,
          exposureGradeLimits
        ),
      }),
    };
  }

  const calcState = buildCalcStateFromProposal(
    state,
    proposal,
    originalFundingPct
  );
  const fixedResult = runCalc(calcState);
  const fixedHasFacilityViolations = calcHasFacilityViolations(state, fixedResult);

  return {
    calcResult: fixedResult,
    autoFix: buildAutoFixMeta({
      attempted: true,
      applied: true,
      fullyFixed: !fixedHasFacilityViolations,
      changeCount: proposal.changes.length,
      firstRemainingViolation: fixedHasFacilityViolations
        ? firstViolationSummary(state, fixedResult, exposureGradeLimits)
        : null,
    }),
  };
}
